import { original, produce } from 'immer';
import {
  AsyncStatus,
  Conversation,
  MetaConversation,
} from 'lib-shared-legacy-types';
import { User } from 'lib-shared-types-api';
import _findIndex from 'lodash/findIndex';
import moment from 'moment-timezone';
import { combineActions, handleActions } from 'redux-actions';

import { appActions } from 'app/app.actions';

import * as visibilityActions from '../../../app/middleware/visibility/actions/visibility';
import { transferToAgentActions } from '../../shared/transferToAgentModal/actions/transferToAgentModal';
import { transferToNumberActions } from '../../shared/transferToNumberModal/actions/transferToNumberModal';
import { transferToQueueActions } from '../../shared/transferToQueueModal/actions/transferToQueueModal';
import * as conversationActions from '../actions/conversation';
import { actions } from '../actions/conversations';
import { updateUserInMetaConversation } from '../utils/updateUserInMetaConversation';

import { emptyMetaConversation } from './conversation';

const { conversationSubjectUpdate } = conversationActions.actions;

// Ws events
const {
  conversationWsIntegrationLoaded,
  conversationWsPending,
  conversationWsAwaitingPending,
  conversationWsCancelPending,
  conversationWsClose,
  conversationWsOpened,
  conversationWsAgentAssigned,
  conversationWsAgentUnassigned,
  conversationWsReassignmentStatus,
} = conversationActions.actions;

const { transferToQueueWsResponse } = transferToQueueActions;

const { transferToNumberWsResponse } = transferToNumberActions;

const { transferToAgentWsResponse } = transferToAgentActions;

const { visibilityHidden } = visibilityActions.actions;

export const {
  conversationsIncreasingUnread,
  conversationsResetUnread,
  conversationsAdd,
  conversationsRemove,
  conversationsUpdateUser,
  conversationsUnassignSelf,
  conversationsSaveTempMessage,
  conversationsSaveTempFormValues,
  conversationsSaveTempWrapup,
  conversationsSaveTempCurrentTab,
  conversationsCloseConversation,
  conversationsConversationToPending,
  conversationsConversationCancelPending,
  conversationsConversationOfferAccepted,
  conversationsConversationClaimed,
  conversationsConversationTransfered,
  conversationsConversationAssistedTransferAccepted,
  conversationsUpdate,
  conversationsSet,
  conversationsConversationChannelChanged,
} = actions;

export type ConversationsState = {
  meta: {
    asyncStatus: AsyncStatus;
  };
  metaConversations: Array<MetaConversation>;
};

// Init state for the Conversations.
export const initialState: ConversationsState = {
  meta: {
    asyncStatus: 'succeeded',
  },
  metaConversations: [],
};

const conversations = handleActions<ConversationsState, any>(
  {
    [visibilityHidden.toString()]: (state: ConversationsState, action) => {
      return produce(state, (draft: ConversationsState) => {
        const index = draft.metaConversations.findIndex(
          (metaConversation: MetaConversation) => {
            return (
              action.payload &&
              action.payload.csid &&
              metaConversation.conversation.id === action.payload.csid
            );
          },
        );

        if (index > -1) {
          draft.metaConversations[index].lastSeen = new Date().getTime();
        }
      });
    },

    [conversationsUpdateUser.toString()]: (
      state: ConversationsState,
      action,
    ) => {
      return produce(state, (draft: ConversationsState) => {
        const {
          payload: { data: updatedUser },
        } = action as { payload: { data: User } };

        for (const draftMetaConversation of draft.metaConversations) {
          Object.assign(
            draftMetaConversation,
            updateUserInMetaConversation(draftMetaConversation, updatedUser),
          );
        }
      });
    },

    [conversationsUnassignSelf.toString()]: (
      state: ConversationsState,
      action: any,
    ) => {
      return produce(state, (draft: ConversationsState) => {
        // We remove ourself from the conversation because it has been move to another queue.
        const csid = action.payload.meta.request.csid;
        const { id, name, is_restricted } =
          action.payload.meta.request.requestToQueue;
        const draftMetaConversations = draft.metaConversations;

        const index = draftMetaConversations.findIndex(
          (metaConversation: MetaConversation) =>
            metaConversation.conversation.id === csid,
        );

        if (index > -1) {
          const draftMetaConv = draft.metaConversations[index];
          draftMetaConv.isAssignedToMe = false;
          draftMetaConv.conversation.is_closed = false;
          draftMetaConv.conversation.status = 'open';
          draftMetaConv.conversation.assignment = null;
          draftMetaConv.conversation.queue = { id, name, is_restricted };
        }
      });
    },

    [conversationsUpdate.toString()]: (state: ConversationsState, action) => {
      return produce(state, (draft: ConversationsState) => {
        for (const { csid, conversation, requester } of action.payload) {
          const newMetaConversation = emptyMetaConversation();

          if (conversation) newMetaConversation.conversation = conversation;
          newMetaConversation.conversation = {
            ...newMetaConversation.conversation,
            id: csid,
          };
          newMetaConversation.isAssignedToMe = true;
          if (requester) newMetaConversation.requester = requester;

          const conversationIndex = draft.metaConversations.findIndex(
            ({ conversation: { id } }) =>
              newMetaConversation.conversation.id === id,
          );
          if (conversationIndex === -1) {
            draft.metaConversations.push(newMetaConversation);
          } else {
            draft.metaConversations[conversationIndex] = {
              ...draft.metaConversations[conversationIndex],
              ...emptyMetaConversation,
            };
          }
        }

        draft.meta.asyncStatus = 'succeeded';
      });
    },

    [conversationsSet.toString()]: (state: ConversationsState, action) => {
      return { ...state, metaConversations: action.payload };
    },

    [conversationsSaveTempMessage.toString()]: (
      state: ConversationsState,
      action,
    ) => {
      return produce(state, (draft: ConversationsState) => {
        const index = draft.metaConversations.findIndex(
          (metaConversation: MetaConversation) =>
            metaConversation.conversation.id === action.payload.csid,
        );
        if (index > -1) {
          draft.metaConversations[index].tempMessage = action.payload.message;
          draft.metaConversations[index].lastSeen = new Date().getTime();
        }
      });
    },

    [conversationsSaveTempFormValues.toString()]: (
      state: ConversationsState,
      action,
    ) => {
      return produce(state, (draft: ConversationsState) => {
        const index = draft.metaConversations.findIndex(
          (metaConversation: MetaConversation) =>
            metaConversation.conversation.id === action.payload.csid,
        );
        if (index > -1) {
          draft.metaConversations[index].tempFormValues =
            action.payload.formValues;
          draft.metaConversations[index].lastSeen = new Date().getTime();
        }
      });
    },

    [conversationsSaveTempWrapup.toString()]: (
      state: ConversationsState,
      action,
    ) => {
      return produce(state, (draft: ConversationsState) => {
        const index = draft.metaConversations.findIndex(
          (metaConversation: MetaConversation) =>
            metaConversation.conversation.id === action.payload.csid,
        );

        if (index > -1) {
          draft.metaConversations[index].tempWrapup = action.payload.message;
          draft.metaConversations[index].lastSeen = new Date().getTime();
        }
      });
    },

    [conversationsSaveTempCurrentTab.toString()]: (
      state: ConversationsState,
      action,
    ) => {
      return produce(state, (draft: ConversationsState) => {
        const index = draft.metaConversations.findIndex(
          (metaConversation: MetaConversation) =>
            metaConversation.conversation.id === action.payload.csid,
        );

        if (index > -1) {
          draft.metaConversations[index].tempCurrentTab =
            action.payload.currentTab;
          draft.metaConversations[index].lastSeen = new Date().getTime();
        }
      });
    },

    [conversationsIncreasingUnread.toString()]: (
      state: ConversationsState,
      action,
    ) => {
      return produce(state, (draft: ConversationsState) => {
        const index = draft.metaConversations.findIndex(
          (metaConversation: MetaConversation) =>
            metaConversation.conversation.id === action.payload.csid,
        );

        if (index > -1) {
          draft.metaConversations[index].unread += 1;
        }
      });
    },

    [conversationsResetUnread.toString()]: (
      state: ConversationsState,
      action,
    ) => {
      return produce(state, (draft: ConversationsState) => {
        const index = draft.metaConversations.findIndex(
          (metaConversation: MetaConversation) =>
            metaConversation.conversation.id === action.payload.csid,
        );

        if (index > -1) {
          draft.metaConversations[index].unread = 0;
        }
      });
    },

    [conversationsCloseConversation.toString()]: (
      state: ConversationsState,
      action,
    ) => {
      return produce(state, (draft: ConversationsState) => {
        const index = draft.metaConversations.findIndex(
          (metaConversation: MetaConversation) =>
            metaConversation.conversation.id === action.payload.data.id,
        );
        if (index > -1) {
          const draftMetaConv = draft.metaConversations[index];
          draftMetaConv.conversation.is_closed = action.payload.data.is_closed;
          draftMetaConv.conversation.closed_at = action.payload.data.closed_at;
          draftMetaConv.conversation.closed_by = action.payload.data.closed_by;
          draftMetaConv.tempMessage = null;
          draftMetaConv.tempFormValues = null;
          draftMetaConv.lastSeen = null;
        }
      });
    },

    [conversationsConversationToPending.toString()]: (
      state: ConversationsState,
      action,
    ) => {
      return produce(state, (draft: ConversationsState) => {
        const index = draft.metaConversations.findIndex(
          (metaConversation: MetaConversation) =>
            metaConversation.conversation.id === action.payload.data.id,
        );
        if (index > -1) {
          const draftMetaConv = draft.metaConversations[index];
          draftMetaConv.conversation.is_closed = action.payload.data.is_closed;
          draftMetaConv.conversation.status = action.payload.data.status;
          draftMetaConv.conversation.assignment =
            action.payload.data.assignment;
          draftMetaConv.conversation.assignments =
            action.payload.data.assignments;
          draftMetaConv.conversation.pending_expiration_at =
            action.payload.data.pending_expiration_at;
        }
      });
    },

    [conversationsConversationCancelPending.toString()]: (
      state: ConversationsState,
      action,
    ) => {
      return produce(state, (draft: ConversationsState) => {
        const index = draft.metaConversations.findIndex(
          (metaConversation: MetaConversation) =>
            metaConversation.conversation.id === action.payload.data.id,
        );
        if (index > -1) {
          const draftMetaConv = draft.metaConversations[index];

          draftMetaConv.conversation.is_closed =
            typeof action.payload.data.is_closed === 'boolean'
              ? action.payload.data.is_closed
              : draftMetaConv.conversation.is_closed;

          draftMetaConv.conversation.status =
            action.payload.data.status || draftMetaConv.conversation.status;

          draftMetaConv.conversation.pending_expiration_at =
            (action.payload.data.pending_expiration_at !== undefined
              ? action.payload.data.pending_expiration_at
              : draftMetaConv.conversation.pending_expiration_at) || null;
        }
      });
    },

    [conversationsRemove.toString()]: (state: ConversationsState, action) => {
      return {
        ...state,
        meta: { ...state.meta, asyncStatus: 'succeeded' },
        metaConversations: state.metaConversations.filter(
          (metaC: MetaConversation) => metaC.conversation.id !== action.payload,
        ),
      };
    },

    [conversationsAdd.toString()]: (state: ConversationsState, action) => {
      if (action.payload.skipStoreAndWs) {
        return state;
      }

      const conversation: Conversation = Object.assign(
        {},
        action.payload.data.history.main_conversation,
      );

      let emptyMetaconversation;
      const oldMetaConversation = action.payload.meta.oldMetaConversation;
      if (
        oldMetaConversation &&
        oldMetaConversation.conversation.id === conversation.id
      ) {
        emptyMetaconversation = Object.assign({}, oldMetaConversation);
      } else {
        emptyMetaconversation = emptyMetaConversation();
      }
      emptyMetaconversation.conversation = conversation; // we add the new conversation to the empty metaConversation.
      if (
        (conversation.assignment &&
          action.payload.meta.userID === conversation.assignment.agent_id) ||
        conversation.requested_by === action.payload.meta.userID
      ) {
        emptyMetaconversation.isAssignedToMe = true;
      } else {
        emptyMetaconversation.isAssignedToMe = false;
      }
      // parentConversation
      if (action.payload.data.history.linked_to?.conversation) {
        emptyMetaconversation.parentConversation =
          action.payload.data.history.linked_to.conversation;
      }
      if (action.payload.data.history.linked_to?.requster) {
        emptyMetaconversation.parentConversationRequester =
          action.payload.data.history.linked_to.requster;
      }
      // previousConversations
      if (action.payload.data.requester_conversations) {
        emptyMetaconversation.previousConversations =
          action.payload.data.requester_conversations;
      }
      // linkedConversations
      if (action.payload.data.requester_conversations) {
        emptyMetaconversation.linkedConversations =
          action.payload.data.history.linked_conversations;
      }

      const requester =
        emptyMetaconversation.conversation.authors[
          emptyMetaconversation.conversation.requested_by
        ];
      if (requester !== undefined) {
        emptyMetaconversation.requester = requester;
      }

      return {
        ...state,
        metaConversations: [
          emptyMetaconversation,
          ...state.metaConversations.filter(
            (metaC: MetaConversation) =>
              metaC.conversation.id !== conversation.id,
          ),
        ],
      };
    },

    [conversationWsIntegrationLoaded.toString()]: (
      state: ConversationsState,
      action,
    ) => {
      return produce(state, (draft: ConversationsState) => {
        const index = draft.metaConversations.findIndex(
          (metaConversation: MetaConversation) =>
            metaConversation.conversation.id === action.payload.params.csid,
        );

        if (index > -1) {
          const payloadIntegration = Object.assign({}, action.payload.params);
          const draftMetaConv = draft.metaConversations[index];

          if (draftMetaConv.integrations) {
            const integrationFromDraftIndex = draft.metaConversations[
              index
            ].integrations.findIndex((i) => i.id === payloadIntegration.id);

            if (integrationFromDraftIndex > -1) {
              draftMetaConv.integrations[integrationFromDraftIndex] =
                payloadIntegration;
            } else {
              draftMetaConv.integrations.push(payloadIntegration);
            }
          }
        }
      });
    },

    [conversationWsClose.toString()]: (state: ConversationsState, action) => {
      return produce(state, (draft: ConversationsState) => {
        const index = draft.metaConversations.findIndex(
          (metaConversation: MetaConversation) =>
            metaConversation.conversation.id === action.payload.params.csid,
        );

        if (index > -1) {
          const draftMetaConv = draft.metaConversations[index];

          draftMetaConv.conversation.is_closed = true;
          draftMetaConv.conversation.closed_by =
            action.payload.params.closed_by;
          draftMetaConv.conversation.closed_at =
            action.payload.params.conversation.closed_at;
          draftMetaConv.conversation.created_at =
            action.payload.params.conversation.created_at;
          draftMetaConv.conversation.live_assesment =
            action.payload.params.live_assesment;
          draftMetaConv.tempMessage = null;
          draftMetaConv.tempFormValues = null;
          draftMetaConv.lastSeen = null;
          draftMetaConv.conversation.status =
            action.payload.params.conversation.status;
        }
      });
    },

    [conversationWsOpened.toString()]: (state: ConversationsState, action) => {
      return produce(state, (draft: ConversationsState) => {
        const index = draft.metaConversations.findIndex(
          (metaConversation: MetaConversation) =>
            metaConversation.conversation.id === action.payload.params.csid,
        );

        if (index > -1) {
          const draftMetaConv = draft.metaConversations[index];

          draftMetaConv.conversation.assignment =
            action.payload.params.conversation.assignment;
          draftMetaConv.isAssignedToMe = false;
          draftMetaConv.conversation.is_closed = false;
          draftMetaConv.conversation.closed_by = null;
          draftMetaConv.conversation.closed_at = null;
          draftMetaConv.tempMessage = null;
          draftMetaConv.tempFormValues = null;
          draftMetaConv.lastSeen = null;
          draftMetaConv.conversation.pending_expiration_at = null;
          draftMetaConv.conversation.live_assesment =
            action.payload.params.live_assesment;
          if (action.payload.params.conversation?.status) {
            draftMetaConv.conversation.status =
              action.payload.params.conversation.status;
          }
          if (action.payload.params.conversation?.initial_channel) {
            draftMetaConv.conversation.initial_channel =
              action.payload.params.conversation.initial_channel;
          }
        }
      });
    },

    [combineActions(
      transferToNumberWsResponse,
      transferToQueueWsResponse,
      transferToAgentWsResponse,
      appActions.conversationTransferRequest.fulfilled.type,
    ).toString()]: (state: ConversationsState, action) => {
      return produce(state, (draft: ConversationsState) => {
        // the payload in `appActions.transferWsResponseReceived` action is
        // in `wsResponse`. If `wsResponse` does not exist, it means that
        // the old actions are triggered, so `action.payload` should be used
        const { csid, accepted, transferee, isAssignedToMe } =
          action.payload.wsResponse ?? action.payload;
        const isAssignedToMeBoolean =
          typeof isAssignedToMe === 'boolean' ? isAssignedToMe : false;

        const index = draft.metaConversations.findIndex(
          (metaConversation: MetaConversation) => {
            return metaConversation.conversation.id === csid;
          },
        );

        if (index > -1 && accepted === true) {
          const draftMetaConv: any = draft.metaConversations[index];

          // We should not update the assignment of the conversation when transferring to non-agents.
          if (transferee?.type === 'contact') {
            draftMetaConv.isAssignedToMe = true;
            return;
          }

          if (draftMetaConv.conversation.assignment !== null) {
            draftMetaConv.conversation.assignment.agent = transferee;

            const originalAgent = original(
              draftMetaConv.conversation.assignment,
            );
            if (draftMetaConv.conversation.assignments) {
              draftMetaConv.conversation.assignments.push({
                assigned_at: moment.utc().format(),
                agent: originalAgent,
                agent_id: originalAgent.id,
                unassigned_at: null,
              });
            }
          } else {
            draftMetaConv.conversation.assignment = {
              agent: transferee,
              agent_id: transferee ? transferee.id : null,
              assigned_at: moment.utc().format(),
            };
          }

          draftMetaConv.isAssignedToMe = isAssignedToMeBoolean;
        }
      });
    },

    [conversationsConversationOfferAccepted.toString()]: (
      state: ConversationsState,
      action,
    ) => {
      return produce(state, (draft: ConversationsState) => {
        const { csid, accepted_by } = action.payload.data;

        const index = draft.metaConversations.findIndex(
          (metaConversation: MetaConversation) => {
            return metaConversation.conversation.id === csid;
          },
        );

        if (index > -1) {
          const draftMetaConv: any = draft.metaConversations[index];

          if (draftMetaConv.conversation.assignment !== null) {
            draftMetaConv.conversation.assignment.agent_id = accepted_by.id;
            draftMetaConv.conversation.assignment.agent = accepted_by;
            draftMetaConv.conversation.assignment.assigned_at = moment
              .utc()
              .format();
          } else {
            draftMetaConv.conversation.assignment = {
              agent_id: accepted_by.id,
              agent: accepted_by,
              assigned_at: moment.utc().format(),
            };
          }
          draftMetaConv.isAssignedToMe = true;
        }
      });
    },

    [conversationsConversationClaimed.toString()]: (
      state: ConversationsState,
      action,
    ) => {
      return produce(state, (draft: ConversationsState) => {
        const { csid, accepted_by } = action.payload.data;

        const index = draft.metaConversations.findIndex(
          (metaConversation: MetaConversation) => {
            return (
              metaConversation.conversation.id === csid && accepted_by !== null
            );
          },
        );

        if (index > -1) {
          const draftMetaConv: any = draft.metaConversations[index];

          if (draftMetaConv.conversation.assignment !== null) {
            draftMetaConv.conversation.assignment.agent_id = accepted_by.id;
            draftMetaConv.conversation.assignment.agent = accepted_by;
            draftMetaConv.conversation.assignment.assigned_at = moment
              .utc()
              .format();
          } else {
            draftMetaConv.conversation.assignment = {
              agent: accepted_by,
              agent_id: accepted_by.id,
              assigned_at: moment.utc().format(),
            };
          }
          draftMetaConv.isAssignedToMe = true;
        }
      });
    },

    [conversationWsPending.toString()]: (state: ConversationsState, action) => {
      return produce(state, (draft: ConversationsState) => {
        const index = draft.metaConversations.findIndex(
          (metaConversation: MetaConversation) => {
            return (
              metaConversation.conversation.id === action.payload.params.csid
            );
          },
        );

        if (index > -1) {
          const draftMetaConv = draft.metaConversations[index];

          draftMetaConv.isAssignedToMe =
            action.payload.params.conversation &&
            action.payload.params.conversation.assignment === null
              ? false
              : draftMetaConv.isAssignedToMe;
          draftMetaConv.conversation.is_closed =
            action.payload.params.conversation &&
            typeof action.payload.params.conversation.is_closed === 'boolean'
              ? action.payload.params.conversation.is_closed
              : draftMetaConv.conversation.is_closed;
          draftMetaConv.conversation.status =
            action.payload.params.conversation &&
            action.payload.params.conversation.status
              ? action.payload.params.conversation.status
              : draftMetaConv.conversation.status;
          draftMetaConv.conversation.assignment =
            action.payload.params.conversation &&
            action.payload.params.conversation.assignment
              ? action.payload.params.conversation.assignment
              : draftMetaConv.conversation.assignment;
          draftMetaConv.conversation.assignments =
            action.payload.params.conversation &&
            action.payload.params.conversation.assignments
              ? action.payload.params.conversation.assignments
              : draftMetaConv.conversation.assignments;
          draftMetaConv.conversation.pending_expiration_at =
            action.payload.params.conversation &&
            action.payload.params.conversation.pending_expiration_at
              ? action.payload.params.conversation.pending_expiration_at
              : null;
          draftMetaConv.conversation.live_assesment =
            action.payload.params.live_assesment;
        }
      });
    },

    [conversationWsAwaitingPending.toString()]: (
      state: ConversationsState,
      action,
    ) => {
      return produce(state, (draft: ConversationsState) => {
        const index = draft.metaConversations.findIndex(
          (metaConversation: MetaConversation) => {
            return (
              metaConversation.conversation.id === action.payload.params.csid
            );
          },
        );
        if (index > -1) {
          const draftMetaConv = draft.metaConversations[index];

          ((draftMetaConv.isAssignedToMe =
            action.payload.params.conversation &&
            action.payload.params.conversation.assignment === null
              ? false
              : draftMetaConv.isAssignedToMe),
            (draftMetaConv.conversation.is_closed =
              action.payload.params.conversation &&
              typeof action.payload.params.conversation.is_closed === 'boolean'
                ? action.payload.params.conversation.is_closed
                : draftMetaConv.conversation.is_closed));
          draftMetaConv.conversation.status =
            action.payload.params.conversation &&
            action.payload.params.conversation.status
              ? action.payload.params.conversation.status
              : draftMetaConv.conversation.status;
          draftMetaConv.conversation.assignment =
            action.payload.params.conversation &&
            action.payload.params.conversation.assignment
              ? action.payload.params.conversation.assignment
              : draftMetaConv.conversation.assignment;
          draftMetaConv.conversation.assignments =
            action.payload.params.conversation &&
            action.payload.params.conversation.assignments
              ? action.payload.params.conversation.assignments
              : draftMetaConv.conversation.assignments;
          draftMetaConv.conversation.pending_expiration_at =
            action.payload.params.conversation &&
            action.payload.params.conversation.pending_expiration_at
              ? action.payload.params.conversation.pending_expiration_at
              : null;
          draftMetaConv.conversation.live_assesment =
            action.payload.params.live_assesment;
        }
      });
    },

    [conversationWsCancelPending.toString()]: (
      state: ConversationsState,
      action,
    ) => {
      return produce(state, (draft: ConversationsState) => {
        const index = draft.metaConversations.findIndex(
          (metaConversation: MetaConversation) => {
            return (
              metaConversation.conversation.id === action.payload.params.csid
            );
          },
        );

        if (index > -1) {
          const draftMetaConv = draft.metaConversations[index];

          draftMetaConv.isAssignedToMe =
            action.payload.params.conversation &&
            action.payload.params.conversation.assignment === null
              ? false
              : draftMetaConv.isAssignedToMe;
          draftMetaConv.conversation.is_closed =
            action.payload.params.conversation &&
            typeof action.payload.params.conversation.is_closed === 'boolean'
              ? action.payload.params.conversation.is_closed
              : draftMetaConv.conversation.is_closed;
          draftMetaConv.conversation.status =
            action.payload.params.conversation &&
            action.payload.params.conversation.status
              ? action.payload.params.conversation.status
              : draftMetaConv.conversation.status;
          draftMetaConv.conversation.assignment =
            action.payload.params.conversation &&
            action.payload.params.conversation.assignment
              ? action.payload.params.conversation.assignment
              : draftMetaConv.conversation.assignment;
          draftMetaConv.conversation.assignments =
            action.payload.params.conversation &&
            action.payload.params.conversation.assignments
              ? action.payload.params.conversation.assignments
              : draftMetaConv.conversation.assignments;
          draftMetaConv.conversation.pending_expiration_at =
            action.payload.params.conversation &&
            action.payload.params.conversation.pending_expiration_at
              ? action.payload.params.conversation.pending_expiration_at
              : null;
          draftMetaConv.conversation.live_assesment =
            action.payload.params.live_assesment;
        }
      });
    },

    [conversationWsAgentAssigned.toString()]: (
      state: ConversationsState,
      action,
    ) => {
      return produce(state, (draft: ConversationsState) => {
        const index = draft.metaConversations.findIndex(
          ({ conversation }: MetaConversation) =>
            conversation &&
            conversation.id === action.payload.csid &&
            conversation.direction !== 'internal',
        );

        if (index > -1) {
          const draftMetaConv: any = draft.metaConversations[index];
          const newAgent = action.payload.agent;
          draftMetaConv.isAssignedToMe =
            action.payload.isAssignedToMe !== undefined &&
            typeof action.payload.isAssignedToMe === 'boolean'
              ? action.payload.isAssignedToMe
              : draftMetaConv.isAssignedToMe;
          if (draftMetaConv.conversation.assignment !== null) {
            draftMetaConv.conversation.assignment.agent_id = newAgent.id;
            draftMetaConv.conversation.assignment.agent = newAgent;
            draftMetaConv.conversation.assignment.assigned_at = moment
              .utc()
              .format();
          } else {
            draftMetaConv.conversation.assignment = {
              agent: newAgent,
              agent_id: newAgent.id,
              assigned_at: moment.utc().format(),
            };
          }
        }
      });
    },

    [conversationWsAgentUnassigned.toString()]: (
      state: ConversationsState,
      action,
    ) => {
      return produce(state, (draft: ConversationsState) => {
        const index = draft.metaConversations.findIndex(
          (metaConversation: MetaConversation) =>
            metaConversation.conversation &&
            metaConversation.conversation.id === action.payload.csid &&
            metaConversation.conversation.direction !== 'internal',
        );

        if (index > -1) {
          const draftMetaConv = draft.metaConversations[index];
          const unassignedAgent = action.payload.agent;
          draftMetaConv.isAssignedToMe =
            action.payload.isAssignedToMe !== undefined &&
            typeof action.payload.isAssignedToMe === 'boolean'
              ? action.payload.isAssignedToMe
              : draftMetaConv.isAssignedToMe;
          if (
            draftMetaConv.conversation.assignment &&
            draftMetaConv.conversation.assignment.agent &&
            unassignedAgent.id ===
              draftMetaConv.conversation.assignment.agent.id
          ) {
            draftMetaConv.conversation.assignment.agent_id = null;
            draftMetaConv.conversation.assignment.agent = null;
            draftMetaConv.conversation.assignment.assigned_at = null;
          }
        }
      });
    },

    [conversationWsReassignmentStatus.toString()]: (
      state: ConversationsState,
      action: {
        payload: {
          csid: number;
          new_agent: User;
          old_agent: User;
          isAssignedToMe: boolean;
        };
      },
    ) => {
      return produce(state, (draft: ConversationsState) => {
        const newAgent = action.payload.new_agent;
        const csid = action.payload.csid;

        const index = draft.metaConversations.findIndex(
          (metaConversation: MetaConversation) =>
            metaConversation.conversation &&
            metaConversation.conversation.id === csid &&
            metaConversation.conversation.direction !== 'internal',
        );

        if (index > -1) {
          const draftMetaConversation: any = draft.metaConversations[index];

          draftMetaConversation.isAssignedToMe =
            action.payload.isAssignedToMe !== undefined &&
            typeof action.payload.isAssignedToMe === 'boolean'
              ? action.payload.isAssignedToMe
              : draftMetaConversation.isAssignedToMe;
          if (draftMetaConversation.conversation.assignment !== null) {
            draftMetaConversation.conversation.assignment.agent_id =
              newAgent.id;
            draftMetaConversation.conversation.assignment.agent = newAgent;
            draftMetaConversation.conversation.assignment.assigned_at = moment
              .utc()
              .format();
          } else {
            draftMetaConversation.conversation.assignment = {
              agent: newAgent,
              agent_id: newAgent.id,
              assigned_at: moment.utc().format(),
            };
          }
        }
      });
    },

    [conversationsConversationTransfered.toString()]: (
      state: ConversationsState,
      action,
    ) => {
      // The request has been successfully transfer to another number.
      return produce(state, (draft: ConversationsState) => {
        const index = draft.metaConversations.findIndex(
          (metaConversation: MetaConversation) =>
            metaConversation.conversation.id === action.payload.csid,
        );

        if (index > -1) {
          draft.metaConversations[index].isAssignedToMe = false;
          draft.metaConversations[index].conversation.assignment = null;
        }
      });
    },

    [conversationsConversationAssistedTransferAccepted.toString()]: (
      state: ConversationsState,
      action,
    ) => {
      // We have accepted a assisted transfer from another number (does this make any sense?!).
      return produce(state, (draft: ConversationsState) => {
        const index = draft.metaConversations.findIndex(
          (metaConversation: MetaConversation) =>
            metaConversation.conversation.id === action.payload.csid,
        );

        if (index > -1) {
          const accepted = action.payload.data.accepted;

          if (
            draft.metaConversations[index].isAssignedToMe === true &&
            accepted === true
          ) {
            draft.metaConversations[index].isAssignedToMe = false;
          }
        }
      });
    },
    [conversationSubjectUpdate.toString()]: (
      state: ConversationsState,
      action,
    ) =>
      produce(state, (draftState: ConversationsState) => {
        const { csid, subject } = action.payload;
        const index = draftState.metaConversations.findIndex(
          (metaConversation: MetaConversation) =>
            metaConversation.conversation.id === csid,
        );

        if (index > -1) {
          if (!draftState.metaConversations[index].conversation.attributes) {
            draftState.metaConversations[index].conversation.attributes = {};
          }
          draftState.metaConversations[index].conversation.attributes.subject =
            subject;
        }
      }),
    [conversationsConversationChannelChanged.toString()]: (
      state: ConversationsState,
      action,
    ) =>
      produce(state, (draftState: ConversationsState) => {
        const { csid, channel_reference } = action.payload;
        const index = draftState.metaConversations.findIndex(
          (metaConversation: MetaConversation) =>
            metaConversation.conversation.id === csid,
        );
        if (index > -1) {
          draftState.metaConversations[index].conversation.initial_channel =
            channel_reference;
        }
      }),
  },
  initialState,
);

export default conversations;
